import { useState } from "react"

import { BookCopy, ChevronDown, FileText, Loader2, Share2, ArrowLeft } from "lucide-react"
import { toast } from "sonner"
import { motion } from "framer-motion"

import { CreateReportGeneratorPanel } from "@/features/report/creation/CreateReportGeneratorPanel"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { downloadDocxFile } from "@/lib/download"
import { useUserSession } from "@session/UserSession"

import { CollectionItem } from "@vertesia/common"

interface ReportActionDropdownProps {
    program: CollectionItem
    download: boolean
    file?: string
}

export function ReportActionDropdown({ program, download, file }: Readonly<ReportActionDropdownProps>) {
    const { client } = useUserSession()

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isDownloading, setIsDownloading] = useState<boolean>(false)
    const [isCopyModalOpen, setIsCopyModalOpen] = useState<boolean>(false)

    const copyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href)
            .then(() => {
                toast.success("Report link copied to clipboard!", {
                    duration: 2000
                })
            })
    }

    const handleExportDocument = async () => {
        if (!file) {
            return
        }

        
        setIsDownloading(true)
        try {
            const obj = await client.store.objects.getObjectText(file)
            await downloadDocxFile(obj.text, program.name)
        } catch (err) {
            console.error(`Failed to export document as docx:`, err)
        } finally {
            setIsDownloading(false)
        }
    }

    const handleCopyClick = () => {
        setIsOpen(false)
        setIsCopyModalOpen(true)
    }

    const handleCloseCopyModal = () => {
        setIsCopyModalOpen(false)
    }

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild disabled={isDownloading}>
                    <Button>
                        {
                            (isDownloading) ? (
                                <>
                                    <Loader2 className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />
                                    <span>Downloading...</span>
                                </>
                            ) : (
                                <>
                                    <span>Actions</span>
                                    <ChevronDown className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                                </>
                            )
                        }
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-primary text-accent">
                    <DropdownMenuItem key={"copy"} onClick={handleCopyClick}>
                        <BookCopy />
                        Re-run Report
                    </DropdownMenuItem>
                    <DropdownMenuItem key={"share"} onClick={copyToClipboard}>
                        <Share2 />
                        Share Report
                    </DropdownMenuItem>
                    {
                        download && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem key={"download"} onClick={handleExportDocument}>
                                    <FileText />
                                    Download as Docx
                                </DropdownMenuItem>
                            </>
                        )
                    }
                </DropdownMenuContent>
            </DropdownMenu>
            {
                isCopyModalOpen && (
                    <div className="z-50 fixed inset-0 flex justify-end top-0">
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-full h-full bg-white shadow-lg p-0 relative"
                        >
                            <CreateReportGeneratorPanel collection={program}>
                                <Button onClick={handleCloseCopyModal}>
                                    <ArrowLeft />
                                    Back to Report
                                </Button>
                            </CreateReportGeneratorPanel>
                        </motion.div>
                    </div>
                )
            }
        </>
    )
}